// MFA policy (MFA configuration & enrolment, 2026-10-06).
//
// Which signed-in, ACTIVE members must (or should) have a second factor before
// the application shell renders. Decided from the authoritative live profile
// (role / superAdmin) and the Firebase user's enrolled factors — never from
// anything the browser stores.
//
//   super-admin  → REQUIRED, no grace (profile flag or token claim; neither is
//                  assigned yet — the super-admin account is currently covered
//                  by its admin role until the claim migration).
//   admin        → REQUIRED after ADMIN_MFA_ENFORCEMENT_AT; until then GRACE:
//                  prompted at every sign-in, may defer.
//   lawyer       → RECOMMENDED (the long-standing product intent: prompted,
//                  may skip).
//   other roles  → NONE (not prompted; no change to existing policy).
// Clients / portal users never reach this code (no FirmAssist account).
//
// This is the client UX gate. Server-side enforcement (rules / callables
// checking firebase.sign_in_second_factor) is a separate, not-yet-authorised step.

export const MFA = Object.freeze({ NONE: 'none', RECOMMENDED: 'recommended', GRACE: 'grace', REQUIRED: 'required' });

// Admin grace period end (30 days after TOTP enablement). Change here only by decision.
export const ADMIN_MFA_ENFORCEMENT_AT = Date.parse('2026-11-06T00:00:00Z');

// Owner decision (2026-10-06): exactly ONE Firebase account — identified by its
// UID, never by email, role, firm or super-admin status — is exempt from
// FirmAssist's requirement to ENROL a second factor. It does not bypass
// Firebase's own sign-in challenge for any factor that is actually enrolled,
// and it grants no authority of any kind.
export const MFA_POLICY_EXEMPT_UIDS = Object.freeze(['uBBdTlGp7sghA3KkJYMFZLyvKDS2']);
export const isMfaPolicyExempt = (uid) => typeof uid === 'string' && MFA_POLICY_EXEMPT_UIDS.includes(uid);

export function mfaRequirement({ uid, role, superAdmin = false, now = Date.now() }) {
  if (isMfaPolicyExempt(uid)) return MFA.NONE;
  if (superAdmin === true) return MFA.REQUIRED;
  if (role === 'admin') return now >= ADMIN_MFA_ENFORCEMENT_AT ? MFA.REQUIRED : MFA.GRACE;
  if (role === 'lawyer') return MFA.RECOMMENDED;
  return MFA.NONE;
}

// Factors that satisfy the requirement. TOTP is the supported/preferred method;
// an existing phone factor (none exist today) is still a valid second factor.
export const hasSecondFactor = (enrolledFactors) => Array.isArray(enrolledFactors) && enrolledFactors.length > 0;

// Should the shell be held behind the enrolment screen?
//   REQUIRED: until a factor is enrolled.
//   GRACE / RECOMMENDED: until enrolled or deferred for this browser session.
export function mfaGateBlocks({ requirement, enrolled, deferred }) {
  if (enrolled || requirement === MFA.NONE) return false;
  if (requirement === MFA.REQUIRED) return true;
  return !deferred;
}
